import { useState } from 'react';
import { SelectField, TextField } from './Form';
import type { QboTermDto } from '../lib/types';

/**
 * A payment term picker searched against this company's own QuickBooks Terms, the same reason
 * AccountLookupField exists for accounts: a Term id is Realm-local, so a free-text box is only
 * usable by someone who already knows it. Lower stakes than an account or tax code — a wrong term
 * changes a due date, not what the ledger says — so this is a plain search-then-pick with no role
 * filtering.
 */
export interface TermLookupFieldProps {
  terms: QboTermDto[];
  loading: boolean;
  selectedId: string | null;
  selectedName: string | null;
  disabled?: boolean;
  onChange: (id: string | null, name: string | null) => void;
}

export function TermLookupField({
  terms: allTerms,
  loading,
  selectedId,
  selectedName,
  disabled,
  onChange,
}: TermLookupFieldProps): JSX.Element {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const matching = term
    ? allTerms.filter((candidate) => candidate.name.toLowerCase().includes(term))
    : allTerms;

  const options = [
    { value: '', label: 'Not mapped' },
    ...matching.map((candidate) => ({ value: candidate.id, label: candidate.name })),
  ];

  if (selectedId && !options.some((option) => option.value === selectedId)) {
    options.splice(1, 0, {
      value: selectedId,
      label: `${selectedName ?? 'Current saved term'} — current selection`,
    });
  }

  const hint = loading
    ? "Loading the connected company's payment terms…"
    : allTerms.length === 0
      ? 'No terms cached yet — use Pull terms above.'
      : 'Type part of a term name to narrow the QuickBooks lookup.';

  return (
    <>
      <TextField
        label="Find term"
        value={search}
        onChange={setSearch}
        placeholder="Search term name"
        disabled={disabled}
      />
      <SelectField
        label="QuickBooks terms"
        value={selectedId ?? ''}
        disabled={disabled}
        onChange={(value) => {
          if (!value) {
            onChange(null, null);
            return;
          }

          const found = allTerms.find((candidate) => candidate.id === value);
          onChange(value, found?.name ?? selectedName);
        }}
        options={options}
        hint={hint}
      />
    </>
  );
}
